import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CreditCard,
  ShieldCheck,
  Lock,
  CheckCircle2,
  TrendingUp,
  Award,
  Plane,
  Building2,
  Factory,
  MapPin,
} from "lucide-react";
import { STRIPE_LINKS } from "@/lib/company";
import StripeBuyButton from "@/components/StripeBuyButton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const DEPLOY_USE_CASES = [
  {
    icon: Building2,
    title: "For Realtors & Property Managers",
    desc: "Hand a luxury vacation voucher to prospective tenants or buyers who walk through your door. Lock in leases instantly and out-pace local competitors.",
  },
  {
    icon: Factory,
    title: "For Manufacturers & Industrial Businesses",
    desc: "Erase your corporate hospitality budget. Reward your top team members with premium vacations at zero extra cost to your bottom line.",
  },
];

const DESTINATION_VIDEOS = [
  {
    src: "/assets/dest-resort.mp4?v=2",
    label: "Luxury Resort Network",
    sub: "Tropical · Beachfront · 5-Star",
  },
  {
    src: "/assets/dest-city.mp4?v=2",
    label: "Premium City & Business Stays",
    sub: "Metropolitan · Business · Boutique",
  },
];

export default function ActivatePage() {
  const [agreed, setAgreed] = useState(false);
  const [agreed2, setAgreed2] = useState(false);
  const [trial1, setTrial1] = useState(false);
  const [trial2, setTrial2] = useState(false);
  const [destOpen, setDestOpen] = useState(false);

  const bothAgreed = agreed && agreed2;
  const trialReady = trial1 && trial2;

  const handleStripe = (e) => {
    if (!agreed) {
      e.preventDefault();
      toast.error("Please agree to the Corporate Sourcing Terms & Conditions before continuing.");
      return false;
    }
    // Let the <a> tag handle navigation naturally — opens Stripe checkout in a new tab
    return true;
  };

  return (
    <div data-testid="activate-page" className="bg-[#020617]">
      {/* HERO */}
      <section className="relative overflow-hidden aios-starfield">
        <div className="absolute inset-0 aios-grid-overlay opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/30 via-transparent to-[#020617]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-28">
          <div className="max-w-4xl">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              OUR BUSINESS MODEL · HOW WE DO IT
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white mt-4">
              Why Is Our Sourcing Service <br />
              <span className="text-red-500">So Aggressively Competitive?</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Let&apos;s cut through the noise. We are an aggressive global sourcing and procurement firm.
              We do not make our margins by marking up individual hotel rooms or charging standard retail broker premiums.
            </p>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              Instead, we use our corporate purchasing power to buy up empty, unbooked luxury hotel inventory worldwide for pennies on the dollar.
              We bundle that elite travel access into an annual subscription and offer it to you at our absolute raw cost &mdash; <span className="text-white font-semibold">$485 total, including tax</span>.
            </p>
          </div>
        </div>
      </section>

      {/* SOURCING SYNERGY */}
      <section className="py-20 lg:py-24 bg-[#0B1221] border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6 text-slate-300 leading-relaxed">
            <div>
              <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
                THE SOURCING SYNERGY
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tighter mt-3">
                By activating your travel portal license, you contractually agree to give us <span className="text-red-500">First Right of Refusal</span> on your company&apos;s sourcing.
              </h2>
            </div>
            <p>
              When you need physical goods, supplies, turnkey setups, property upkeep, or outsourced labor, you give us the opportunity to quote it.
            </p>
            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              <div className="aios-card p-6" data-testid="synergy-zero-risk">
                <div className="aios-card-accent" />
                <div className="flex items-center gap-2 text-red-500 font-display tracking-widest text-xs font-bold">
                  <ShieldCheck className="w-4 h-4" /> ZERO RISK
                </div>
                <p className="text-slate-300 mt-3 leading-relaxed">
                  If we cannot beat your current supplier&apos;s pricing or quality, you are <span className="text-white font-semibold">100% free to use them.</span>
                </p>
              </div>
              <div className="aios-card p-6" data-testid="synergy-massive-reward">
                <div className="aios-card-accent" />
                <div className="flex items-center gap-2 text-red-500 font-display tracking-widest text-xs font-bold">
                  <TrendingUp className="w-4 h-4" /> MASSIVE REWARD
                </div>
                <p className="text-slate-300 mt-3 leading-relaxed">
                  If we beat your current vendors, you save thousands on baseline procurement while continuing to enjoy <span className="text-white font-semibold">unlimited corporate travel benefits.</span>
                </p>
              </div>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="aios-card p-6">
              <div className="aios-card-accent" />
              <Award className="w-7 h-7 text-red-500" />
              <h4 className="font-display font-bold text-white text-lg mt-4">Corporate-Tier Access</h4>
              <p className="text-slate-400 text-sm mt-2">Pricing usually locked behind enterprise procurement contracts.</p>
            </div>
            <div className="aios-card p-6">
              <div className="aios-card-accent" />
              <Plane className="w-7 h-7 text-red-500" />
              <h4 className="font-display font-bold text-white text-lg mt-4">Unlimited 4-5 Day Vouchers</h4>
              <p className="text-slate-400 text-sm mt-2">Generate as many vouchers as you need for the next twelve months.</p>
            </div>
          </aside>
        </div>
      </section>

      {/* CORPORATE INCENTIVE */}
      <section className="py-20 lg:py-28" data-testid="travel-incentive-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              THE ULTIMATE CORPORATE INCENTIVE
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
              Wipe Out Corporate Travel Costs <br />
              <span className="text-red-500">&amp; Close Deals Faster.</span>
            </h2>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed">
              Your $485 corporate license unlocks <span className="text-white font-semibold">Unlimited 4-to-5 Day Luxury Hotel Vouchers</span>.
              Strategic business assets to lock in leases, reward employees, or entertain high-value clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {DEPLOY_USE_CASES.map((u, idx) => {
              const Icon = u.icon;
              return (
                <div key={idx} className="aios-card p-8 lg:p-10" data-testid={`activate-deploy-${idx}`}>
                  <div className="aios-card-accent" />
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-600/10 border border-red-600/30">
                      <Icon className="w-6 h-6 text-red-500" />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg lg:text-xl tracking-tight">
                      {u.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-slate-400 leading-relaxed">{u.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 p-5 border-l-2 border-red-600 bg-slate-900/40 text-sm text-slate-400 max-w-4xl">
            <span className="font-display text-xs tracking-widest text-red-500 font-bold uppercase">Note</span>
            <p className="mt-2 leading-relaxed">
              Your subscription covers the baseline luxury room rate entirely. The traveler is only responsible for standard local nightly government hotel taxes and resort fees at activation.
            </p>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/destinations"
              data-testid="travel-cta-destinations"
              className="aios-btn-secondary text-sm inline-flex"
            >
              <Plane className="w-4 h-4" />
              [ VIEW THE 130+ DESTINATIONS ]
            </Link>
          </div>

          {/* Destinations Modal */}
          <Dialog open={destOpen} onOpenChange={setDestOpen}>
            <DialogContent
              data-testid="destinations-dialog"
              className="bg-[#0B1221] border border-slate-700 text-white max-w-5xl p-0 overflow-hidden"
            >
              <DialogHeader className="p-6 lg:p-8 border-b border-slate-800 text-left">
                <DialogTitle className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-white">
                  <span className="text-red-500">130+ Luxury Destinations.</span> One Portal.
                </DialogTitle>
                <DialogDescription className="text-slate-400 text-sm md:text-base mt-2">
                  A preview of the corporate travel inventory unlocked by your $485 portal activation — resorts, business hotels, beachfront escapes, all pre-negotiated at corporate rates.
                </DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-0">
                {DESTINATION_VIDEOS.map((v, idx) => (
                  <div
                    key={idx}
                    data-testid={`destination-video-${idx}`}
                    className="relative aspect-video bg-black border-t border-slate-800 sm:border-t-0 sm:border-r last:border-r-0"
                  >
                    <video
                      src={v.src}
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3 bg-red-600 px-3 py-1 font-display text-[10px] tracking-[0.25em] text-white font-bold pointer-events-none">
                      {String(idx + 1).padStart(2, "0")} · {v.label.toUpperCase()}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent pointer-events-none">
                      <div className="font-display font-bold text-white">{v.label}</div>
                      <div className="flex items-center gap-1 text-slate-300 text-xs mt-1">
                        <MapPin className="w-3 h-3 text-red-500" /> {v.sub}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-6 lg:p-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-slate-400 text-sm">
                  Activate your portal to access the full searchable destination inventory.
                </p>
                <button
                  data-testid="destinations-activate-cta"
                  onClick={() => {
                    setDestOpen(false);
                    setTimeout(() => {
                      document.getElementById("secure-portal")?.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                  }}
                  className="aios-btn-primary text-xs whitespace-nowrap"
                >
                  Activate Portal · $485
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* NO-RISK GUARANTEE */}
      <section className="py-20 bg-[#0B1221] border-y border-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="aios-card p-10 lg:p-14 text-center" data-testid="activate-guarantee-card">
            <div className="aios-card-accent" />
            <ShieldCheck className="w-12 h-12 text-red-500 mx-auto" />
            <span className="block mt-6 text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              THE NO-RISK GUARANTEE
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white mt-4 tracking-tighter">
              The Sourcing Proof Guarantee
            </h2>
            <p className="mt-5 text-slate-300 max-w-3xl mx-auto leading-relaxed">
              A single night at a standard 4-star hotel costs more than $485. By generating just one voucher for a client, tenant, or employee, <span className="text-white font-semibold">your corporate license has already paid for itself</span>.
              You legally maintain your unlimited travel access even if your current suppliers happen to out-bid our sourcing specialists on a project quote.
            </p>
          </div>
        </div>
      </section>

      {/* ============ TWO-TIER PRICING MATRIX ============ */}
      <section id="secure-portal" className="py-20 lg:py-28" data-testid="activate-checkout-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              TWO-TIER PRICING MATRIX
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3 tracking-tighter">
              Evaluate first, or <span className="text-red-500">activate unlimited</span>.
            </h2>
            <p className="mt-5 text-slate-400 text-base">
              Both cards require dual verification. Both buttons remain locked until you check both boxes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* CARD A · $9.95 EVALUATION */}
            <div className="bg-white text-slate-900 border-t-4 border-slate-400 shadow-xl p-8 md:p-10" data-testid="pricing-card-trial">
              <div className="font-display text-[10px] tracking-[0.3em] font-bold text-slate-500 uppercase">CARD A · SYSTEM EVALUATION TRIAL</div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold mt-2 tracking-tight">Evaluation Voucher</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">$9.95</span>
                <span className="text-slate-500 text-sm">USD · one-time</span>
              </div>

              {/* Auto-renewal disclaimer */}
              <div className="mt-5 p-3 bg-amber-50 border border-amber-300 text-[12px] text-amber-900 leading-snug" data-testid="trial-auto-renew-warning">
                <strong className="font-bold">⚠ AUTOMATIC RENEWAL NOTICE:</strong> By purchasing this trial, you authorize a one-time charge of $9.95. After 14 days, your card on file will be billed $485 for an annual license unless canceled by contacting <a href="mailto:support@allinsource.com" className="underline">support@allinsource.com</a> before the trial window expires.
              </div>

              <div className="mt-6 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={trial1} onCheckedChange={(v) => setTrial1(Boolean(v))} id="trial1"
                    data-testid="trial-checkbox-1"
                    className="mt-1 border-slate-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                  <span className="text-[13px] text-slate-700 leading-relaxed">
                    I understand the $9.95 evaluation package is a one-time purchase to audit the destination portal. The voucher covers baseline room rates, and I am responsible for local nightly tax and resort recovery fees ($34 to $89/night) at booking.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={trial2} onCheckedChange={(v) => setTrial2(Boolean(v))} id="trial2"
                    data-testid="trial-checkbox-2"
                    className="mt-1 border-slate-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                  <span className="text-[13px] text-slate-700 leading-relaxed">
                    I agree to the standard Corporate Terms of Service and explicitly acknowledge that the provider is held completely harmless from any occurrences, damages, injuries, or liabilities involving our hotel room stays.
                  </span>
                </label>
              </div>

              <div className="mt-6" data-testid="trial-stripe-wrap">
                {trialReady ? (
                  <StripeBuyButton
                    buyButtonId="buy_btn_1TnYJC2Ck4RqpKdWlvwRbth9"
                    publishableKey="pk_live_51SIcAO2Ck4RqpKdWGR4SgW6FYKx72BX5XKlwBWIYPFtWn5ciIrg8QrJ1tG1CCCJMYqmZCWgj7axnWbee7146lxKc00noaWg8Gh"
                    testId="trial-stripe-btn"
                  />
                ) : (
                  <button disabled className="w-full font-display font-bold uppercase tracking-widest text-sm py-4 px-6 bg-slate-300 text-slate-500 cursor-not-allowed" data-testid="trial-stripe-locked">
                    🔒 [ PURCHASE EVALUATION VOUCHER — $9.95 ]
                  </button>
                )}
                {!trialReady && (
                  <p className="mt-2 text-[11px] text-red-600 text-center font-semibold">Check both boxes above to unlock checkout.</p>
                )}
              </div>
            </div>

            {/* CARD B · $485 CORPORATE LICENSE */}
            <div className="bg-white text-slate-900 border-t-4 border-red-600 shadow-[0_25px_80px_-15px_rgba(220,38,38,0.3)] p-8 md:p-10 relative" data-testid="pricing-card-license">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white font-display text-[10px] tracking-[0.3em] font-bold px-4 py-1 uppercase">MOST POPULAR</div>
              <div className="font-display text-[10px] tracking-[0.3em] font-bold text-red-600 uppercase">CARD B · UNLIMITED ANNUAL CORPORATE LICENSE</div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold mt-2 tracking-tight">Corporate License</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">$485</span>
                <span className="text-slate-500 text-sm">/ year · unlimited vouchers</span>
              </div>

              <ul className="mt-5 space-y-2 text-slate-700 text-[13px]">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />Unlimited 4-to-5 day luxury vouchers · 130+ destinations</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />18-month execution window per voucher</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />Non-exclusive First Right of Refusal sourcing partnership</li>
              </ul>

              <div className="mt-6 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(Boolean(v))} id="lic1"
                    data-testid="activate-terms-checkbox"
                    className="mt-1 border-slate-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                  <span className="text-[13px] text-slate-700 leading-relaxed">
                    I agree to the Corporate Sourcing Terms and agree to grant a First Right of Refusal to review and submit competitive bids on our company sourcing and supply chain needs.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={agreed2} onCheckedChange={(v) => setAgreed2(Boolean(v))} id="lic2"
                    data-testid="activate-terms-checkbox-2"
                    className="mt-1 border-slate-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                  <span className="text-[13px] text-slate-700 leading-relaxed">
                    I explicitly acknowledge that the unlimited vouchers cover baseline luxury room rates in full, that individual travelers are solely responsible for local nightly taxes and resort fees ($34–$89/night), and that the provider carries zero liability involving room stays.
                  </span>
                </label>
              </div>

              <div className="mt-6" data-testid="activate-stripe-wrap">
                {bothAgreed ? (
                  <StripeBuyButton
                    buyButtonId="buy_btn_1TnYFx2Ck4RqpKdWP6DnJwoL"
                    publishableKey="pk_live_51SIcAO2Ck4RqpKdWGR4SgW6FYKx72BX5XKlwBWIYPFtWn5ciIrg8QrJ1tG1CCCJMYqmZCWgj7axnWbee7146lxKc00noaWg8Gh"
                    testId="activate-stripe-btn"
                  />
                ) : (
                  <button disabled className="w-full font-display font-bold uppercase tracking-widest text-sm py-4 px-6 bg-slate-300 text-slate-500 cursor-not-allowed" data-testid="activate-stripe-locked">
                    🔒 [ ACTIVATE UNLIMITED CORPORATE LICENSE — $485 ]
                  </button>
                )}
                {!bothAgreed && (
                  <p className="mt-2 text-[11px] text-red-600 text-center font-semibold">Check both boxes above to unlock checkout.</p>
                )}
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-slate-500">
                <div className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-red-600" /> SSL Secured</div>
                <div className="flex items-center gap-1"><Lock className="w-3 h-3 text-red-600" /> Stripe Verified</div>
                <div>PCI-DSS</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
